import { createShouldForwardPropBlockList, deepMerge } from "@components/common/utils/helper";
import {
    getBackgroundColor,
    getBorderRadius,
    getBoxShadow,
    getBoxShadowFocus,
    getColor,
    getIconSize,
    getSize,
    getVerticalCenter,
} from "./input-checkbox.styles.utils";
import { type InputCheckboxProps } from "./input-checkbox";
import styled from "styled-components";

type ContainerProps = Pick<InputCheckboxProps, "size" | "verticalCenter">;
type CheckboxProps = Pick<InputCheckboxProps, "size" | "checkSwitch">;
type WrapperProps = Pick<InputCheckboxProps, "color" | "shadow" | "error" | "checkSwitch">;
type CheckerProps = Pick<InputCheckboxProps, "size" | "checkSwitch" | "color" | "disabled" | "rounded" | "shadow" | "error">;
type CheckerIconProps = Pick<InputCheckboxProps, "size" | "checkSwitch" | "rounded">;

export const Container = styled("label").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "verticalCenter"]),
})<ContainerProps>(({ size, verticalCenter, theme }) => ({
    display: "flex",
    gap: theme.spacing[3],
    ...getVerticalCenter(size, verticalCenter, theme),
}));

export const Checkbox = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "checkSwitch"]),
})<CheckboxProps>(({ size, checkSwitch, theme }) => ({
    position: "relative",
    verticalAlign: "top",
    flexShrink: 0,
    ...deepMerge(getSize(size, checkSwitch, theme)),
}));

export const Wrapper = styled("input").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["color", "size", "shadow", "error", "checkSwitch"]),
})<WrapperProps>(({ color, shadow, error, checkSwitch, theme }) => ({
    position: "absolute",
    opacity: 0,
    top: "0",
    "&:focus": {
        "& + div": {
            ...deepMerge(getBoxShadowFocus(color, error, false, theme)),
            outline: "none",
        },
    },
    "&:disabled": {
        "& + div": {
            pointerEvents: "none",
            opacity: 0.5,
        },
    },
    "&:checked": {
        "& + div": {
            ...deepMerge(getBackgroundColor(color, checkSwitch, true, theme), getBoxShadow(shadow, color, error, true, theme)),
        },
        "& + div > svg": {
            opacity: 1,
            ...(checkSwitch ? { scale: "90%", left: "50%" } : {}),
        },
        "&:focus": {
            "& + div": {
                ...deepMerge(getBoxShadowFocus(color, error, true, theme)),
            },
        },
    },
}));

export const Checker = styled("div").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "checkSwitch", "color", "disabled", "rounded", "shadow", "error"]),
})<CheckerProps>(({ size, checkSwitch, color, disabled, rounded, shadow, error, theme }) => ({
    position: "relative",
    verticalAlign: "top",
    cursor: "pointer",
    alignItems: "start",
    ...deepMerge(
        getSize(size, checkSwitch, theme),
        getBackgroundColor(color, checkSwitch, false, theme),
        getColor(color, theme),
        getBorderRadius(rounded, theme),
        getBoxShadow(shadow, color, error, false, theme),
    ),
    transitionProperty: "background-color, box-shadow, color, opacity, transform",
    transitionDuration: "300ms",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    textDecoration: "none !important",
    display: "inline-flex",
    fontWeight: theme.fontWeight.medium,
    outline: "none",
    whiteSpace: "nowrap",
    justifyContent: "center",
    ...(disabled && { pointerEvents: "none", opacity: 0.5 }),
}));

export const CheckerIcon = styled("svg").withConfig({
    shouldForwardProp: createShouldForwardPropBlockList(["size", "checkSwitch", "rounded"]),
})<CheckerIconProps>(({ size, checkSwitch, rounded, theme }) => ({
    transitionProperty: "background-color, box-shadow, opacity, transform, translate, left",
    transitionDuration: "300ms",
    transitionTimingFunction: theme.transitionTimingFunction["out-cubic"],
    ...deepMerge({
        ...(checkSwitch && {
            position: "absolute",
            top: 0,
            backgroundColor: theme.colors.white,
            boxShadow: theme.boxShadow.DEFAULT,
            left: 0,
            ...getBorderRadius(rounded, theme),
        }),
        ...(!checkSwitch && {
            position: "absolute",
            top: "50%",
            opacity: 0,
            left: "50%",
            translate: "-50%  -50%",
        }),
        ...getIconSize(size, checkSwitch, theme),
    }),
}));

export const Description = styled.div(() => ({
    width: "100%",
}));
